import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { fireConfetti } from '../../lib/animations'
import { notifyCouple } from '../../lib/notifications'
import { Heart, Check, X, Download } from 'lucide-react'

// Five theme-colour shades for the confirmation card. Each guest gets one
// deterministically (same shade every time for that guest, varied across guests).
const CARD_SHADES = [
  ['#2B1A0E', '#160C05'], // espresso
  ['#4A2E1F', '#2A1810'], // cocoa
  ['#3D4A3A', '#212B20'], // deep sage
  ['#5A3320', '#311A10'], // sienna
  ['#3E2A33', '#21161C'], // mulberry
]
function pickShade(seed) {
  let h = 0
  const s = seed || ''
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0
  return CARD_SHADES[h % CARD_SHADES.length]
}
function roundRectPath(ctx, x, y, w, h, r) {
  if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(x, y, w, h, r); return }
  ctx.beginPath()
  ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath()
}

export default function RSVPPage() {
  const { token } = useParams()
  const [guest, setGuest]     = useState(null)
  const [wedding, setWedding] = useState(null)
  const [events, setEvents]   = useState([])
  const [loading, setLoading] = useState(true)
  const [submitted, setSubmitted] = useState(false)
  const [response, setResponse]   = useState(null)
  const [saving, setSaving]       = useState(false)
  const [error, setError]         = useState('')
  const [coverPhoto, setCoverPhoto]   = useState(null)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    async function load() {
      const { data: g } = await supabase.rpc('get_guest_by_token', { p_token: token })
      if (!g) { setLoading(false); return }
      setGuest(g)
      if (g.rsvp_status === 'attending' || g.rsvp_status === 'confirmed') { setSubmitted(true); setResponse('attending') }
      else if (g.rsvp_status === 'declined' || g.rsvp_status === 'rejected') { setSubmitted(true); setResponse('declined') }
      const { data: w } = await supabase.rpc('get_public_wedding', { p_id: g.wedding_id })
      if (w) {
        setWedding(w)
        // The public wedding strips venues from strictly-invited events. This guest
        // holds a valid invite token, so fetch the real venues and merge them in.
        let evs = w.wedding_events || []
        try {
          const { data: full } = await supabase.rpc('get_invite_events', { p_token: token })
          if (Array.isArray(full) && full.length) {
            const venueById = Object.fromEntries(full.map(e => [e.id, e.venue]))
            evs = evs.map(e => e.venue ? e : { ...e, venue: venueById[e.id] ?? e.venue })
          }
        } catch (_) { /* fall back to whatever the public fetch gave */ }
        setEvents(evs)
      }
      // Studio photo for the card — inlined as a data URL so it can be drawn
      // into the downloaded PNG without tainting the canvas.
      let cover = w?.story_cover_url || null
      if (!cover) {
        const { data: ph } = await supabase.from('photos').select('public_url')
          .eq('wedding_id', g.wedding_id).eq('is_studio_photo', true).eq('approved', true).limit(1)
        cover = ph?.[0]?.public_url || null
      }
      if (cover) {
        try {
          const blob = await (await fetch(cover)).blob()
          cover = await new Promise(resolve => {
            const fr = new FileReader()
            fr.onload  = () => resolve(fr.result)
            fr.onerror = () => resolve(null)
            fr.readAsDataURL(blob)
          })
        } catch { cover = null }
      }
      setCoverPhoto(cover)
      setLoading(false)
    }
    load()
  }, [token])

  async function confirm(status) {
    setSaving(true); setError('')
    const { data: ok, error: err } = await supabase.rpc('submit_rsvp', { p_token: token, p_status: status })
    if (err || !ok) { setError("That didn't work — try again or tap the chat bubble and we'll sort it out."); setSaving(false); return }
    setSubmitted(true); setResponse(status); setSaving(false)
    if (status === 'attending' || status === 'confirmed') {
      fireConfetti()
      notifyCouple(guest.wedding_id, '🎉 New RSVP!', `${guest.name} will be there`, 'rsvp')
    } else {
      notifyCouple(guest.wedding_id, 'RSVP Update', `${guest.name} can't make it`, 'rsvp')
    }
  }

  // Render the card with the native Canvas API (no html2canvas → no font/CORS
  // failures, always produces a downloadable PNG).
  async function downloadCard() {
    if (downloading) return
    setDownloading(true)
    try {
      try { await document.fonts.ready } catch {}
      const scale = 2, W = 680
      const evLines = guestEvents.slice(0, 2)
      const H = 520 + evLines.length * 56
      const canvas = document.createElement('canvas')
      canvas.width = W * scale; canvas.height = H * scale
      const ctx = canvas.getContext('2d')
      ctx.scale(scale, scale)
      const cx = W / 2, accent = '#C4826A', cream = '#FAF0E6'
      const setLS = v => { try { ctx.letterSpacing = v } catch {} }

      const grad = ctx.createLinearGradient(0, 0, W, H)
      grad.addColorStop(0, shade[0]); grad.addColorStop(1, shade[1])
      ctx.fillStyle = grad; roundRectPath(ctx, 0, 0, W, H, 30); ctx.fill()

      // Studio photo at 80% multiply over the shade (drawn from the data URL)
      if (coverPhoto) {
        const coverImg = await new Promise(res => {
          const im = new Image(); im.onload = () => res(im); im.onerror = () => res(null); im.src = coverPhoto
        })
        if (coverImg) {
          ctx.save()
          roundRectPath(ctx, 0, 0, W, H, 30); ctx.clip()
          ctx.globalAlpha = 0.8
          ctx.globalCompositeOperation = 'multiply'
          const iw = coverImg.naturalWidth || coverImg.width, ih = coverImg.naturalHeight || coverImg.height
          const sc = Math.max(W / iw, H / ih)
          const dw = iw * sc, dh = ih * sc
          ctx.drawImage(coverImg, (W - dw) / 2, (H - dh) / 2, dw, dh)
          ctx.restore()
        }
      }

      ctx.textAlign = 'center'
      let y = 80

      ctx.fillStyle = 'rgba(196,130,106,0.75)'
      ctx.font = '700 19px "Nunito Sans", sans-serif'; setLS('4px')
      ctx.fillText("IT'S OFFICIAL", cx, y); setLS('0px'); y += 66

      ctx.fillStyle = cream
      let fs = 46; ctx.font = `400 ${fs}px Gibs, Georgia, serif`
      while (ctx.measureText(guestName).width > W - 110 && fs > 24) { fs -= 2; ctx.font = `400 ${fs}px Gibs, Georgia, serif` }
      ctx.fillText(guestName, cx, y); y += 42

      ctx.fillStyle = 'rgba(250,240,230,0.6)'
      ctx.font = '400 21px "Nunito Sans", sans-serif'
      ctx.fillText('will be attending', cx, y); y += 46

      ctx.strokeStyle = 'rgba(196,130,106,0.3)'; ctx.lineWidth = 1
      ctx.beginPath(); ctx.moveTo(70, y); ctx.lineTo(W - 70, y); ctx.stroke(); y += 40

      ctx.fillStyle = 'rgba(196,130,106,0.72)'
      ctx.font = '700 16px "Nunito Sans", sans-serif'; setLS('3px')
      ctx.fillText('THE WEDDING OF', cx, y); setLS('0px'); y += 36

      ctx.fillStyle = accent
      ctx.font = '400 30px Gibs, Georgia, serif'
      ctx.fillText(`${p1} & ${p2}`, cx, y); y += 34

      ctx.beginPath(); ctx.moveTo(70, y); ctx.lineTo(W - 70, y); ctx.stroke(); y += 44

      for (const ev of evLines) {
        if (ev.date) {
          ctx.fillStyle = 'rgba(250,240,230,0.7)'
          ctx.font = '400 19px "Nunito Sans", sans-serif'
          ctx.fillText(new Date(ev.date).toLocaleDateString('en-GB', { weekday:'long', day:'numeric', month:'long', year:'numeric' }), cx, y); y += 26
        }
        if (ev.venue) {
          ctx.fillStyle = 'rgba(250,240,230,0.48)'
          ctx.font = '400 17px "Nunito Sans", sans-serif'
          ctx.fillText(ev.venue, cx, y); y += 30
        } else { y += 8 }
      }

      ctx.fillStyle = 'rgba(250,240,230,0.4)'
      ctx.font = '600 15px "Nunito Sans", sans-serif'
      ctx.fillText('Planned with ♥ by Okoiya', cx, H - 40)

      const blob = await new Promise(res => canvas.toBlob(res, 'image/png'))
      if (!blob) throw new Error('toBlob returned null')
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `rsvp-${(p1 || 'wedding').toLowerCase().replace(/\s+/g, '-')}.png`
      document.body.appendChild(link); link.click(); document.body.removeChild(link)
      setTimeout(() => URL.revokeObjectURL(url), 1500)
    } catch (err) {
      console.error('Card download failed:', err)
      alert('Could not save automatically — please screenshot the card instead.')
    }
    setDownloading(false)
  }

  if (loading) return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#BCBCBC' }}>Loading your invitation…</p>
    </div>
  )

  if (!guest || !wedding) return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', display:'flex', alignItems:'center', justifyContent:'center', textAlign:'center', padding:'2rem' }}>
      <div>
        <p style={{ fontFamily:'Gibs, serif', fontSize:'1.1rem', color:'#1A1A1A' }}>This invite link is invalid or has expired.</p>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', color:'#6B6B6B', fontSize:'0.875rem' }}>Please contact the couple for assistance.</p>
      </div>
    </div>
  )

  const p1 = wedding.partner1_name, p2 = wedding.partner2_name
  const guestName = guest.plus_one ? `Mr & Mrs ${guest.name}` : guest.name
  const shade = pickShade(token)
  const guestEvents = events.filter(e =>
    !guest.event_id && (!guest.invited_event_ids || guest.invited_event_ids.length === 0)
      ? true
      : guest.invited_event_ids?.length > 0
        ? guest.invited_event_ids.includes(e.id)
        : e.id === guest.event_id
  )

  return (
    <div style={{ minHeight:'100vh', background:'#FAFAF8', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'1.25rem' }}>
      <div style={{ width:'100%', maxWidth:420, background:'white', border:'1px solid #E5E0D8', borderRadius:'1.5rem', overflow:'hidden', boxShadow:'0 8px 40px rgba(0,0,0,0.08)' }}>

        {/* Header */}
        <div style={{ background:'linear-gradient(160deg,#1A1A1A,#2A1A10)', padding:'2.25rem 1.75rem', textAlign:'center' }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', background:'rgba(196,130,106,0.15)', borderRadius:100, padding:'0.3rem 0.875rem', marginBottom:'1.25rem' }}>
            <Heart size={11} color="#C4826A" fill="rgba(196,130,106,0.4)"/>
            <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.65rem', fontWeight:700, color:'rgba(196,130,106,0.9)', letterSpacing:'0.1em', textTransform:'uppercase' }}>You're Invited</span>
          </div>
          <h2 style={{ fontFamily:'Gibs, serif', fontSize:'clamp(1.9rem,6vw,2.4rem)', color:'white', fontWeight:400, lineHeight:1.15, margin:'0 0 0.2rem' }}>{p1}</h2>
          <p style={{ fontFamily:'Gibs, serif', fontStyle:'italic', fontSize:'0.9rem', color:'rgba(196,130,106,0.8)', margin:'0 0 0.2rem' }}>&amp;</p>
          <h2 style={{ fontFamily:'Gibs, serif', fontSize:'clamp(1.9rem,6vw,2.4rem)', color:'white', fontWeight:400, lineHeight:1.15, margin:0 }}>{p2}</h2>
        </div>

        {/* Body */}
        <div style={{ padding:'1.75rem 1.5rem' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#6B6B6B', margin:'0 0 0.2rem', textAlign:'center' }}>Dear</p>
          <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.2rem', color:'#1A1A1A', textAlign:'center', margin:'0 0 0.75rem' }}>{guestName}</p>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.84rem', color:'#6B6B6B', textAlign:'center', lineHeight:1.7, margin:'0 0 1.25rem' }}>
            It is with joyful hearts that <strong style={{ color:'#1A1A1A' }}>{p1} &amp; {p2}</strong> invite you to celebrate their wedding. Your presence would mean the world to them{guestEvents.length>0?' at the celebration'+(guestEvents.length>1?'s':''):''} below.
          </p>

          {guestEvents.length > 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem', marginBottom:'1.5rem' }}>
              {guestEvents.map(ev => (
                <div key={ev.id} style={{ background:'#FFFFFF', border:'1px solid #E5E0D8', borderRadius:'0.75rem', padding:'0.75rem 1rem' }}>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.72rem', color:'#C4826A', textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 0.3rem' }}>{ev.type}</p>
                  {ev.date && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#1A1A1A', margin:'0 0 0.15rem' }}>📅 {new Date(ev.date).toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</p>}
                  {ev.venue && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B', margin:0 }}>📍 {ev.venue}</p>}
                </div>
              ))}
            </div>
          )}

          {submitted ? (
            response === 'attending' || response === 'confirmed' ? (
              /* ── Shareable confirmation card ── */
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'0.75rem', padding:'0.25rem 0' }}>
                <style>{`
                  @keyframes rsvpCardIn {
                    from { opacity:0; transform:scale(0.94) translateY(8px); }
                    to   { opacity:1; transform:scale(1)    translateY(0); }
                  }
                `}</style>
                {/* The card itself — optimised for screenshots */}
                <div style={{
                  position:'relative', overflow:'hidden',
                  width:'100%', background:`linear-gradient(160deg, ${shade[0]}, ${shade[1]})`,
                  borderRadius:'1.125rem', padding:'1.75rem 1.5rem',
                  textAlign:'center', animation:'rsvpCardIn 0.45s cubic-bezier(0.34,1.56,0.64,1) both',
                }}>
                  {coverPhoto && (
                    <img src={coverPhoto} alt=""
                      style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover', opacity:0.8, mixBlendMode:'multiply', pointerEvents:'none' }}/>
                  )}
                  <div style={{ position:'relative', zIndex:1 }}>
                  {/* Decorative top ornament */}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem', marginBottom:'1.25rem' }}>
                    <div style={{ flex:1, height:'0.5px', background:'linear-gradient(to right,transparent,rgba(196,130,106,0.4))' }}/>
                    <span style={{ fontFamily:'Gibs, serif', fontSize:'0.75rem', color:'rgba(196,130,106,0.7)', letterSpacing:'0.18em', textTransform:'uppercase' }}>It's Official</span>
                    <div style={{ flex:1, height:'0.5px', background:'linear-gradient(to left,transparent,rgba(196,130,106,0.4))' }}/>
                  </div>

                  <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.35rem', color:'#FAF0E6', margin:'0 0 0.2rem', lineHeight:1.25 }}>{guestName}</p>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'rgba(250,240,230,0.55)', margin:'0 0 1.25rem', letterSpacing:'0.04em' }}>will be attending</p>

                  <div style={{ borderTop:'0.5px solid rgba(196,130,106,0.25)', borderBottom:'0.5px solid rgba(196,130,106,0.25)', padding:'0.875rem 0', margin:'0 0 1.25rem' }}>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.65rem', color:'rgba(196,130,106,0.65)', letterSpacing:'0.14em', textTransform:'uppercase', margin:'0 0 0.35rem' }}>The Wedding of</p>
                    <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.05rem', color:'#C4826A', margin:0 }}>{p1} &amp; {p2}</p>
                  </div>

                  {guestEvents.length > 0 && (
                    <div style={{ display:'flex', flexDirection:'column', gap:'0.35rem', marginBottom:'1.25rem' }}>
                      {guestEvents.slice(0,2).map(ev=>(
                        <div key={ev.id}>
                          {ev.date && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'rgba(250,240,230,0.65)', margin:0 }}>
                            📅 {new Date(ev.date).toLocaleDateString('en-GB',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
                          </p>}
                          {ev.venue && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'rgba(250,240,230,0.45)', margin:'0.15rem 0 0' }}>📍 {ev.venue}</p>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Okoiya brand footer */}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'0.4rem', marginTop:'0.5rem' }}>
                    <div style={{ flex:1, height:'0.5px', background:'rgba(255,255,255,0.06)' }}/>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', color:'rgba(250,240,230,0.25)', margin:0 }}>
                      Planned with ♥ by{' '}
                      <a href="https://okoiya.com" target="_blank" rel="noreferrer" style={{ color:'rgba(196,130,106,0.6)', textDecoration:'none', fontWeight:600 }}>
                        Okoiya
                      </a>
                    </p>
                    <div style={{ flex:1, height:'0.5px', background:'rgba(255,255,255,0.06)' }}/>
                  </div>
                  </div>{/* /content layer */}
                </div>

                <button onClick={downloadCard} disabled={downloading}
                  style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.8rem', color:'white', background:'#C4826A', border:'none', borderRadius:100, padding:'0.7rem 1.4rem', cursor: downloading?'default':'pointer', opacity: downloading?0.7:1 }}>
                  <Download size={14}/> {downloading ? 'Saving…' : 'Save my card'}
                </button>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#6B6B6B', margin:0, textAlign:'center', lineHeight:1.65 }}>
                  📸 Share it on your story — and <strong style={{ color:'#1A1A1A' }}>save this card</strong>. Show it at the entrance on the day: it's your check-in.
                </p>
              </div>
            ) : (
              /* ── Declined state ── */
              <div style={{ textAlign:'center', padding:'1rem 0' }}>
                <div style={{ width:52, height:52, background:'rgba(196,130,106,0.1)', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 0.875rem' }}>
                  <X size={22} color="#C4826A"/>
                </div>
                <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.1rem', color:'#1A1A1A', margin:'0 0 0.4rem' }}>Sorry to miss you</h3>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B', lineHeight:1.65 }}>
                  Your response has been recorded. {p1} &amp; {p2} will miss you.
                </p>
              </div>
            )
          ) : (
            <div>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#6B6B6B', textAlign:'center', margin:'0 0 1.125rem', lineHeight:1.65 }}>Kindly confirm your attendance</p>
              {error && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#C4826A', textAlign:'center', marginBottom:'0.75rem' }}>{error}</p>}
              <div style={{ display:'flex', flexDirection:'column', gap:'0.625rem' }}>
                <button onClick={()=>confirm('attending')} disabled={saving} className="btn-primary"
                  style={{ width:'100%', justifyContent:'center', display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.875rem' }}>
                  <Check size={16}/> {saving?'Locking that in…':"Yes, I'll be there! 🎉"}
                </button>
                <button onClick={()=>confirm('declined')} disabled={saving} className="btn-secondary"
                  style={{ width:'100%', justifyContent:'center', display:'flex', alignItems:'center', gap:'0.4rem', padding:'0.875rem' }}>
                  <X size={15}/> Can't make it
                </button>
              </div>
            </div>
          )}
        </div>

        <div style={{ padding:'0.875rem 1.5rem', borderTop:'1px solid #E5E0D8', textAlign:'center' }}>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.62rem', color:'#BCBCBC', margin:0 }}>
            This invite is personal to you · Powered by{' '}
            <a href="https://okoiya.com" target="_blank" rel="noreferrer" style={{ color:'#C4826A', textDecoration:'none', fontWeight:600 }}>Okoiya</a>
          </p>
        </div>
      </div>
    </div>
  )
}
